import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ShoppingBag, Menu, X, Phone } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { Button } from './ui/button';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from './ui/sheet';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_89039fd3-cb90-48f9-88ab-9d6547dca827/artifacts/ang7mnbh_d04b0c90-39ed-4f51-93a8-9960dfd45601.jpg';

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { cartCount, setIsCartOpen } = useCart();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Shop', path: '/shop' },
    { name: 'Contact', path: '/contact' },
  ];

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <header
      className={`fixed top-[36px] left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-wild-sand/95 backdrop-blur-md shadow-sm'
          : 'bg-wild-sand'
      }`}
      data-testid="header"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-20">
          {/* Mobile Menu Button */}
          <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
            <SheetTrigger asChild className="md:hidden">
              <Button
                variant="ghost"
                size="icon"
                className="text-wild-dark hover:text-wild-leather"
                data-testid="mobile-menu-button"
              >
                <Menu className="h-6 w-6" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-[300px] bg-wild-sand">
              <SheetHeader>
                <SheetTitle className="font-headings text-2xl text-wild-leather">
                  WILD ONES LLC
                </SheetTitle>
              </SheetHeader>
              <nav className="flex flex-col gap-6 mt-10">
                {navLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={`font-headings text-xl tracking-wider transition-colors ${
                      isActive(link.path)
                        ? 'text-wild-leather'
                        : 'text-wild-dark hover:text-wild-leather'
                    }`}
                    data-testid={`mobile-nav-${link.name.toLowerCase()}`}
                  >
                    {link.name}
                  </Link>
                ))}
                <a
                  href="tel:8032725881"
                  className="flex items-center gap-2 font-body text-wild-stone hover:text-wild-leather transition-colors mt-4"
                  data-testid="mobile-phone-link"
                >
                  <Phone className="h-4 w-4" />
                  803-272-5881
                </a>
              </nav>
            </SheetContent>
          </Sheet>

          {/* Logo */}
          <Link
            to="/"
            className="flex items-center gap-3"
            data-testid="header-logo"
          >
            <img
              src={LOGO_URL}
              alt="Wild Ones LLC"
              className="h-14 w-14 object-contain"
            />
            <span className="hidden sm:block font-headings text-2xl tracking-wider text-wild-dark">
              WILD ONES
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-10">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`font-headings text-lg tracking-wider transition-colors relative group ${
                  isActive(link.path)
                    ? 'text-wild-leather'
                    : 'text-wild-dark hover:text-wild-leather'
                }`}
                data-testid={`nav-${link.name.toLowerCase()}`}
              >
                {link.name}
                <span
                  className={`absolute -bottom-1 left-0 h-0.5 bg-wild-leather transition-all duration-300 ${
                    isActive(link.path) ? 'w-full' : 'w-0 group-hover:w-full'
                  }`}
                />
              </Link>
            ))}
          </nav>

          {/* Right Section */}
          <div className="flex items-center gap-4">
            {/* Phone - Desktop */}
            <a
              href="tel:8032725881"
              className="hidden lg:flex items-center gap-2 font-body text-sm text-wild-stone hover:text-wild-leather transition-colors"
              data-testid="header-phone"
            >
              <Phone className="h-4 w-4" />
              803-272-5881
            </a>

            {/* Cart Button */}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsCartOpen(true)}
              className="relative text-wild-dark hover:text-wild-leather"
              data-testid="cart-button"
            >
              <ShoppingBag className="h-6 w-6" />
              {cartCount > 0 && (
                <span
                  className="absolute -top-1 -right-1 bg-wild-leather text-white text-xs w-5 h-5 rounded-full flex items-center justify-center font-body"
                  data-testid="cart-count"
                >
                  {cartCount}
                </span>
              )}
            </Button>
          </div>
        </div>
      </div>
    </header>
  );
};
